// ReferralsPanel — 2026-05-20 Agent Referral Dashboard.
//
// Lives at /rewards/referrals as the third sub-tab of RewardsHub
// (alongside Browse + Exposure). Lets an agent see every referral
// where the REFERRER is a user inside their downline tree — direct
// players plus every sub-agent's players, recursively.
//
// Referral bonuses are 100% admin-funded. No agent wallet is debited
// here. This panel is read-only insight: who's growing the agent's
// network, who they referred, and how much the platform paid out.
//
// Backend: GET /admin/me-agent/referrals[?status=pending|credited|rejected]
// Returns { summary, rows, total, limit, offset } — see the
// meAgentDashboardController.referrals controller for the exact shape.

import { useEffect, useState } from 'react';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import { useT } from '@baaligames/shared-ui/lib/i18n';

type Status = 'pending' | 'credited' | 'rejected';
type StatusFilter = Status | 'all';

type Referral = {
  id: number;
  status: Status;
  bonusAmount: number;
  createdAt: string;
  creditedAt: string | null;
  rejectionReason: string | null;
  triggeredByRechargeId: number | null;
  referrer: {
    id: number;
    name: string;
    username: string | null;
    referralCode: string | null;
    agentId: number | null;
  };
  referred: {
    id: number;
    name: string;
    username: string | null;
    agentId: number | null;
  };
};

type Response = {
  summary: {
    total: number;
    pending: number;
    credited: number;
    rejected: number;
    total_paid: number;
  };
  rows: Referral[];
  total: number;
  limit: number;
  offset: number;
};

const inr = (n: number) => `₹${(n || 0).toLocaleString('en-IN')}`;

const fmtDate = (iso: string | null) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: '2-digit' });
  } catch { return iso; }
};

// Status pill colour family. Mirrors the admin-side referrals queue so
// an agent who later sees the same record on a parent's screen reads
// the same colour vocabulary.
const STATUS_STYLE: Record<Status, { dot: string; text: string; bg: string; ring: string }> = {
  pending:  { dot: 'bg-amber-400',   text: 'text-amber-700 dark:text-amber-200',
              bg: 'bg-amber-100/70 dark:bg-amber-500/15',
              ring: 'ring-amber-300/60 dark:ring-amber-400/30' },
  credited: { dot: 'bg-emerald-400', text: 'text-emerald-700 dark:text-emerald-200',
              bg: 'bg-emerald-100/70 dark:bg-emerald-500/15',
              ring: 'ring-emerald-300/60 dark:ring-emerald-400/30' },
  rejected: { dot: 'bg-rose-400',    text: 'text-rose-700 dark:text-rose-200',
              bg: 'bg-rose-100/70 dark:bg-rose-500/15',
              ring: 'ring-rose-300/60 dark:ring-rose-400/30' },
};

export default function ReferralsPanel() {
  const t = useT();
  const [data, setData] = useState<Response | null>(null);
  const [err, setErr]   = useState<string | null>(null);
  const [filter, setFilter] = useState<StatusFilter>('all');

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setErr(null);
    (async () => {
      try {
        const q = filter === 'all' ? '' : `?status=${filter}`;
        const r = await adminApi.get<Response>(`/admin/me-agent/referrals${q}`);
        if (!cancelled) setData(r);
      } catch (e) {
        if (!cancelled) setErr((e as ApiError).message
          || t('agent.referrals.error.loadFailed') || 'Failed to load referrals.');
      }
    })();
    return () => { cancelled = true; };
  }, [filter, t]);

  if (err) {
    return (
      <div className="rounded-xl bg-rose-500/10 border border-rose-400/30
                      text-rose-700 dark:text-rose-200 text-sm px-4 py-3">
        {err}
      </div>
    );
  }

  if (!data) {
    return (
      <div className="grid place-items-center py-12 text-arena-600 dark:text-neon-200/70 text-sm">
        <Icon name="progress_activity" size={24} className="animate-spin" />
        <p className="mt-2">{t('agent.referrals.loading')}</p>
      </div>
    );
  }

  const s = data.summary;
  const isEmpty = data.rows.length === 0;

  return (
    <div className="space-y-4">
      {/* Hero — summary statistics. U1/U4 (2026-07-09): no JS opacity
          entrance — content must be readable from first paint. */}
      <div
        className="relative overflow-hidden rounded-2xl
                   bg-gradient-to-br from-violet-500/20 via-violet-500/10 to-transparent
                   border border-violet-300/40 dark:border-violet-400/30
                   px-4 py-4 sm:px-5 sm:py-5"
      >
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <div className="absolute -top-1/3 -right-1/4 w-2/3 h-2/3 bg-violet-400/20 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="hub" size={16} className="text-violet-700 dark:text-violet-300" filled />
            <div className="text-[10px] uppercase tracking-[.22em] font-extrabold
                            text-violet-700 dark:text-violet-200">
              {t('agent.referrals.hero.kicker')}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <SummaryStat label={t('agent.referrals.stat.total')} value={s.total} tone="neutral" />
            <SummaryStat label={t('agent.referrals.status.credited')} value={s.credited} tone="ok" />
            <SummaryStat label={t('agent.referrals.status.pending')} value={s.pending} tone="warn" />
            <SummaryStat label={t('agent.referrals.status.rejected')} value={s.rejected} tone="bad" />
          </div>
          <div className="mt-3 pt-3 border-t border-white/10 dark:border-white/10
                          flex items-baseline justify-between gap-3">
            <span className="text-[11px] uppercase tracking-[.18em]
                             text-arena-600 dark:text-neon-200/70 font-bold">
              {t('agent.referrals.hero.paidLabel')}
            </span>
            <span className="font-display font-black text-xl sm:text-2xl tabular-nums
                             text-emerald-700 dark:text-emerald-300
                             drop-shadow-[0_2px_8px_rgba(16,185,129,0.25)]">
              {inr(s.total_paid)}
            </span>
          </div>
          <p className="mt-2 text-[10.5px] text-arena-600/80 dark:text-neon-200/60 leading-relaxed">
            {t('agent.referrals.hero.explainer')}
          </p>
        </div>
      </div>

      {/* Filter chips — keeps the URL clean (state lives in the panel,
          not the route) so back-button returns to the parent tab. */}
      <div className="flex items-center gap-2 px-1">
        {(['all', 'credited', 'pending', 'rejected'] as StatusFilter[]).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-full text-[11px] font-bold uppercase
                        tracking-wider transition
                        ${filter === f
                          ? 'bg-violet-600 text-white shadow-[0_2px_10px_rgba(139,92,246,0.4)]'
                          : 'bg-white/60 dark:bg-white/[0.06] text-arena-700 dark:text-neon-200/80 hover:bg-white dark:hover:bg-white/[0.1] ring-1 ring-arena-200/60 dark:ring-white/10'}`}
          >
            {f === 'all' ? t('agent.referrals.filter.all', { n: s.total }) : t(`agent.referrals.status.${f}`)}
          </button>
        ))}
      </div>

      {/* Rows — single-column cards. Mobile-first. Wider screens get the
          same card width with more breathing room (hub's max-w-3xl
          parent already caps the container). */}
      {isEmpty ? (
        <div className="rounded-xl bg-white/60 dark:bg-white/[0.04]
                        ring-1 ring-arena-200/60 dark:ring-white/10
                        px-4 py-8 grid place-items-center
                        text-arena-600 dark:text-neon-200/70 text-sm">
          <Icon name="inbox" size={32} className="opacity-60 mb-2" />
          <p className="text-center">
            {filter === 'all'
              ? t('agent.referrals.empty.all')
              : t('agent.referrals.empty.filtered', { status: t(`agent.referrals.status.${filter}`) })}
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {data.rows.map((r) => (
            <ReferralCard key={r.id} row={r} />
          ))}
        </ul>
      )}

      {data.total > data.rows.length && (
        <p className="text-[11px] text-arena-600 dark:text-neon-200/60 text-center">
          {t('agent.referrals.pagination', { shown: data.rows.length, total: data.total })}
        </p>
      )}
    </div>
  );
}

function SummaryStat({
  label, value, tone,
}: {
  label: string;
  value: number;
  tone: 'neutral' | 'ok' | 'warn' | 'bad';
}) {
  const colour = {
    neutral: 'text-arena-900 dark:text-white',
    ok:      'text-emerald-700 dark:text-emerald-300',
    warn:    'text-amber-700 dark:text-amber-300',
    bad:     'text-rose-700 dark:text-rose-300',
  }[tone];
  return (
    <div className="rounded-xl bg-white/50 dark:bg-white/[0.04]
                    ring-1 ring-arena-200/50 dark:ring-white/10
                    px-3 py-2.5">
      <div className="text-[9.5px] uppercase tracking-[.18em] font-bold
                      text-arena-600 dark:text-neon-200/70">
        {label}
      </div>
      <div className={`font-display font-black text-xl tabular-nums ${colour}`}>
        {value.toLocaleString('en-IN')}
      </div>
    </div>
  );
}

function ReferralCard({ row }: { row: Referral }) {
  const t = useT();
  const s = STATUS_STYLE[row.status];
  return (
    <li className="rounded-xl bg-white/70 dark:bg-white/[0.04]
                   ring-1 ring-arena-200/60 dark:ring-white/10
                   px-3.5 py-3 space-y-2">
      {/* Top row: referrer → referred + bonus / status */}
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-[12.5px] text-arena-900 dark:text-white">
            <span className="font-bold truncate">{row.referrer.name}</span>
            <Icon name="arrow_forward" size={12} className="text-arena-500 dark:text-neon-200/60 shrink-0" />
            <span className="font-bold truncate">{row.referred.name}</span>
          </div>
          <div className="mt-0.5 text-[10.5px] uppercase tracking-wider
                          text-arena-500 dark:text-neon-200/60 truncate">
            {row.referrer.referralCode
              ? <>{t('agent.referrals.card.code')} <span className="font-mono">{row.referrer.referralCode}</span></>
              : <>{t('agent.referrals.card.referred', { date: fmtDate(row.createdAt) })}</>}
            {row.creditedAt && (
              <> · {t('agent.referrals.card.credited', { date: fmtDate(row.creditedAt) })}</>
            )}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-display font-black text-base sm:text-lg tabular-nums
                          text-emerald-700 dark:text-emerald-300">
            {row.status === 'credited' ? inr(row.bonusAmount) : '—'}
          </div>
          <div className="text-[9px] uppercase tracking-wider
                          text-arena-500 dark:text-neon-200/55">
            {t('agent.referrals.card.adminPaid')}
          </div>
        </div>
      </div>

      {/* Bottom row: status pill + meta (rejection reason if any) */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className={`inline-flex items-center gap-1.5 text-[10px]
                          uppercase tracking-wider font-bold
                          px-2 py-0.5 rounded-full ring-1
                          ${s.bg} ${s.text} ${s.ring}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
          {t(`agent.referrals.status.${row.status}`)}
        </span>
        {row.status === 'rejected' && row.rejectionReason && (
          <span className="text-[10.5px] text-rose-700/80 dark:text-rose-300/80
                           truncate max-w-[60%]" title={row.rejectionReason}>
            {row.rejectionReason}
          </span>
        )}
        {row.triggeredByRechargeId && (
          <span className="text-[10px] tabular-nums text-arena-500 dark:text-neon-200/60">
            {t('agent.referrals.card.recharge', { id: row.triggeredByRechargeId })}
          </span>
        )}
      </div>
    </li>
  );
}
